import { useEffect, useMemo, useRef } from 'react'
import { useQuery, UseQueryResult } from 'react-query'

import { BillStatus } from '@features/bills/enums/billStatus'
import { Bill } from '@features/bills/types/bill'
import { CegidIntegrationStatus } from '@features/cegidIntegration/enums/cegidIntegrationStatus'
import { EInvoiceStatus } from '@features/eInvoicing/constants'

import { defaultStaleTime } from '../../../../../../config/queryClient'
import { SortDirection } from '../../../../../../enums/sortDirection'
import { usePaginationPrefetching } from '../../../../../../hooks/usePaginationPrefetching'
import { Paging } from '../../../../../../types/metable'
import { formatDateToString } from '../../../../../../utils/formatDateToString'
import { billStatusToQueryProps } from '../../../../constants/billStatusToQueryProps'
import { BillsSortProperty, fetchBills, FetchBillsResponseData } from '../../../../query-api'
import { BillsQueryProps } from '../../../../types/billsQueryProps'
import { getBillsQueryKey } from '../../../../utils/getBillsQueryKey'

export interface UseFetchBillsProps {
  organizationId: string
  isDisabled?: boolean
  filters?: {
    contactId?: string
    currencyId?: string
    eInvoiceStatus?: EInvoiceStatus[]
    minReceiptDate?: Date | null
    maxReceiptDate?: Date | null
    status?: BillStatus
    syncStatus?: CegidIntegrationStatus[]
  }
  pagination?: {
    page: number
    pageSize: number
  }
  searchQuery?: string
  sorting?: {
    direction: SortDirection
    key: BillsSortProperty
  }
}

export type UseFetchBillsResponse = Omit<UseQueryResult<FetchBillsResponseData, unknown>, 'data'> & {
  bills: Bill[]
  pagination?: Paging
}

export const useFetchBills = ({
  isDisabled,
  organizationId,
  pagination,
  searchQuery,
  sorting,
  filters,
}: UseFetchBillsProps): UseFetchBillsResponse => {
  const previousPagination = useRef<Paging | undefined>(undefined)

  const queryProps = useMemo<BillsQueryProps>(
    () => ({
      // Joins
      include:
        'bill.contact:embed,bill.attachments:embed,attachment.file:embed,bill.lines:embed,bill.balanceModifiers.balanceModifier.modifier:embed,bill.integrationStatus:embed',

      // Filters
      ...(filters?.contactId ? { contactId: filters.contactId } : {}),
      ...(filters?.currencyId ? { currencyId: filters.currencyId } : {}),
      ...(filters?.status ? billStatusToQueryProps[filters.status] : {}),
      ...(filters?.syncStatus ? { latestCpaSyncStatus: filters.syncStatus } : {}),
      ...(filters?.eInvoiceStatus ? { pdpEInvoiceStatus: filters.eInvoiceStatus } : {}),
      // Billy API expects YYYY-MM-DD, which means we should format the localized string and not convert to UTC
      ...(filters?.minReceiptDate ? { minEntryDate: formatDateToString(filters.minReceiptDate) } : {}),
      ...(filters?.maxReceiptDate ? { maxEntryDate: formatDateToString(filters.maxReceiptDate) } : {}),

      // Pagination
      ...(pagination?.page ? { page: pagination.page } : {}),
      ...(pagination?.pageSize ? { pageSize: pagination.pageSize } : {}),

      // Sorting
      ...(sorting?.direction ? { sortDirection: sorting.direction } : {}),
      ...(sorting?.key ? { sortProperty: sorting.key } : {}),

      // Search
      ...(searchQuery ? { q: searchQuery } : {}),

      // Required
      organizationId,
    }),
    [organizationId, pagination, filters, searchQuery, sorting],
  )
  const queryKey = useMemo(() => getBillsQueryKey(queryProps), [queryProps])

  const { data, ...rest } = useQuery(queryKey, () => fetchBills(queryProps), {
    enabled: !!organizationId && !isDisabled,
    staleTime: defaultStaleTime,
  })

  // If pagination exists then copy the value to reference and use it when original pagination during loading returns `undefined` value.
  useEffect(() => {
    if (data?.meta.paging) {
      previousPagination.current = { ...data?.meta.paging }
    }
  }, [data?.meta.paging])

  usePaginationPrefetching<BillsQueryProps, FetchBillsResponseData>({
    getQueryFunction: useMemo(() => fetchBills, []),
    getQueryKey: useMemo(() => getBillsQueryKey, []),
    isEnabled: !!organizationId && !rest.isLoading && !isDisabled,
    queryProps,
    totalPages: data?.meta.paging.pageCount as number,
  })

  return {
    ...rest,
    bills: data?.bills || [],
    pagination: data?.meta.paging || previousPagination.current,
  }
}
